import React, { useState, useEffect } from 'react';
import { Video, Calendar, Clock, Link as LinkIcon, Plus, Trash2, Edit2, X, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from '../api';

const MeetingManager = () => {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        link: '',
        meeting_date: '',
        meeting_time: '',
        status: true
    });

    useEffect(() => {
        fetchMeetings();
    }, []);

    const fetchMeetings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/meetings');
            setMeetings(response.data.meetings || []);
        } catch (error) {
            console.error('Error fetching meetings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (meeting = null) => {
        if (meeting) {
            setEditingMeeting(meeting);
            setFormData({
                title: meeting.title,
                link: meeting.link,
                meeting_date: meeting.meeting_date,
                meeting_time: meeting.meeting_time,
                status: meeting.status
            });
        } else {
            setEditingMeeting(null);
            setFormData({
                title: '',
                link: '',
                meeting_date: '',
                meeting_time: '',
                status: true
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingMeeting) {
                await api.put(`/admin/meetings/${editingMeeting.id}`, formData);
            } else {
                await api.post('/admin/meetings', formData);
            }
            toast.success(editingMeeting ? 'Meeting updated successfully' : 'Meeting created successfully');
            fetchMeetings();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error saving meeting:', error);
            toast.error('Failed to save meeting. Please check the data.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Meeting?',
            text: "This will remove the meeting schedule for all employees.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/admin/meetings/${id}`);
                fetchMeetings();
                toast.success('Meeting deleted successfully');
            } catch (error) {
                console.error('Error deleting meeting:', error);
                toast.error('Failed to delete meeting');
            }
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto space-y-4 animate-in fade-in duration-500">
            {/* Reduced Height Header (Pati) */}
            <div className="flex items-center justify-between bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-black text-slate-800 flex items-center gap-3">
                    <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                        <Video size={20} />
                    </div>
                    Meeting Management
                </h2>
                <button 
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-emerald-600 transition-all shadow-sm active:scale-95"
                >
                    <Plus size={18} />
                    Schedule New Meeting
                </button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-100 italic text-gray-400">
                    <Loader2 className="animate-spin text-emerald-500 mb-2" size={32} />
                    Loading meetings...
                </div>
            ) : meetings.length > 0 ? (
                /* Row-wise layout for meetings */
                <div className="flex flex-col gap-3">
                    {meetings.map((meeting) => (
                        <div key={meeting.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-100 transition-all flex items-center justify-between gap-6 group">
                            
                            <div className="flex items-center gap-4 flex-1 overflow-hidden">
                                <div className="p-3 bg-slate-50 text-emerald-600 rounded-xl group-hover:bg-emerald-50 transition-colors shrink-0">
                                    <Video size={22} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="text-[15px] font-black text-slate-800 capitalize truncate">{meeting.title}</h3>
                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider shrink-0 ${meeting.status ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {meeting.status ? 'Active' : 'Expired'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-5">
                                        <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold">
                                            <Calendar size={14} className="text-slate-300" />
                                            {new Date(meeting.meeting_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold">
                                            <Clock size={14} className="text-slate-300" />
                                            {meeting.meeting_time ? new Date(`1970-01-01T${meeting.meeting_time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '--'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Meeting Link - Row Style */}
                            <div className="hidden lg:flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100 max-w-[300px] flex-1">
                                <LinkIcon size={14} className="text-slate-400 shrink-0" />
                                <a href={meeting.link} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-emerald-600 hover:underline truncate">{meeting.link}</a>
                            </div>

                            <div className="flex items-center gap-2">
                                <button onClick={() => handleOpenModal(meeting)} className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" title="Edit Meeting">
                                    <Edit2 size={16} />
                                </button>
                                <button onClick={() => handleDelete(meeting.id)} className="p-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" title="Delete Meeting">
                                    <Trash2 size={16} />
                                </button>
                                <a href={meeting.link} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors lg:hidden" title="Join Meeting">
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-xl p-12 text-center border border-gray-100 shadow-sm">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                        <Video size={30} />
                    </div>
                    <h4 className="text-base font-bold text-slate-800">No Meetings Scheduled</h4>
                    <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto font-medium">Create a new meeting to share links and schedules with your employees.</p>
                </div>
            )}

            {/* Schedule Modal with Backdrop Blur spanning entire screen */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[28px] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="bg-[#1c2536] p-6 text-white flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold">{editingMeeting ? 'Update' : 'Schedule'} Meeting</h3>
                                <p className="text-xs text-slate-400 font-medium">Enter meeting details for employees</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Meeting Title</label>
                                <input 
                                    type="text" 
                                    required
                                    placeholder="Weekly Sync Up / Training Session"
                                    className="w-full bg-slate-50 border border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all capitalize"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Meeting Link (URL)</label>
                                <div className="relative">
                                    <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                                    <input 
                                        type="url" 
                                        required
                                        placeholder="https://meet.google.com/..."
                                        className="w-full bg-slate-50 border border-gray-100 rounded-xl pl-11 pr-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-mono"
                                        value={formData.link}
                                        onChange={(e) => setFormData({...formData, link: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Date</label>
                                    <input 
                                        type="date" 
                                        required
                                        className="w-full bg-slate-50 border border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                        value={formData.meeting_date}
                                        onChange={(e) => setFormData({...formData, meeting_date: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Time</label>
                                    <input 
                                        type="time" 
                                        required
                                        className="w-full bg-slate-50 border border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                        value={formData.meeting_time}
                                        onChange={(e) => setFormData({...formData, meeting_time: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex items-center gap-3">
                                <button 
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-gray-100 text-sm font-bold text-gray-400 hover:bg-gray-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-[1.5] bg-[#1c2536] text-white px-4 py-3 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={18} /> : editingMeeting ? 'Update Meeting' : 'Schedule Now'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MeetingManager;
